// src/pages/Login.js
import React, { useState, useRef, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { Link, useNavigate } from "react-router-dom";

function parseHashTokens() {
  // Supabase sends tokens in URL hash, e.g. /login#access_token=...&refresh_token=...&type=recovery
  const hash = window.location.hash?.replace(/^#/, "");
  const params = new URLSearchParams(hash);
  return {
    access_token: params.get("access_token"),
    refresh_token: params.get("refresh_token"),
    type: params.get("type"), // 'recovery' for password reset
  };
}

export default function Login() {
  const nav = useNavigate();

  // --- normal login state ---
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(false); // UI only for now
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");

  // --- forgot/reset email sender state ---
  const [sendingLink, setSendingLink] = useState(false);

  // --- inline recovery state (when user clicked the email link) ---
  const [isRecovery, setIsRecovery] = useState(false);
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [resetMsg, setResetMsg] = useState("");   // success/info for reset UI
  const [resetErr, setResetErr] = useState("");   // error for reset UI

  const emailRef = useRef(null);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  // Detect tokens in URL hash, set the session, then flip into recovery UI
  useEffect(() => {
    (async () => {
      const { access_token, refresh_token, type } = parseHashTokens();
      if (access_token && refresh_token) {
        const { error } = await supabase.auth.setSession({ access_token, refresh_token });
        // Clean tokens from the URL bar
        window.history.replaceState({}, "", window.location.pathname);
        if (!error && type === "recovery") {
          setIsRecovery(true);
        }
      }
    })();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setNote("");
    setLoading(true);

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    setLoading(false);
    if (error) {
      setNote(error.message);
      return;
    }

    // Supabase persists the session; `remember` is just UI for now
    nav("/dashboard");
  };

  const onReset = async () => {
    // Sends the email with a redirect back to THIS page so we can handle tokens inline
    setNote("");
    setResetErr("");
    setResetMsg("");

    if (!email) {
      setNote("Enter your email first.");
      return;
    }

    setSendingLink(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + "/login",
    });
    setSendingLink(false);

    if (error) {
      setNote(error.message);
      return;
    }
    setNote("If that email exists, we sent a reset link. Check inbox/spam.");
  };

  async function onSetNewPassword(e) {
    e.preventDefault();
    setResetErr("");
    setResetMsg("");

    if (pw1.length < 8) {
      setResetErr("Password must be at least 8 characters.");
      return;
    }
    if (pw1 !== pw2) {
      setResetErr("Passwords do not match.");
      return;
    }

    setSavingPw(true);
    const { error } = await supabase.auth.updateUser({ password: pw1 });
    setSavingPw(false);

    if (error) {
      setResetErr(error.message || "Could not update password.");
    } else {
      setResetMsg("Password updated! Redirecting to login…");
      setTimeout(async () => {
        await supabase.auth.signOut();
        window.location.href = "/login";
      }, 1200);
    }
  }

  // If the user came from the email and we set a session, show the inline password reset form
  if (isRecovery) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-950/95 p-8 shadow-[0_24px_60px_rgba(15,23,42,0.95)]">
          <h1 className="text-2xl font-bold tracking-tight text-slate-50">
            Choose a new password
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            Enter and confirm your new password below.
          </p>

          {resetErr && (
            <div className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-100">
              {resetErr}
            </div>
          )}
          {resetMsg && (
            <div className="mt-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-100">
              {resetMsg}
            </div>
          )}

          <form onSubmit={onSetNewPassword} className="mt-6 space-y-4">
            <div>
              <label
                htmlFor="pw1"
                className="mb-1 block text-sm font-medium text-slate-200"
              >
                New password
              </label>
              <input
                id="pw1"
                type="password"
                placeholder="New password"
                className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none ring-sky-500 focus:ring-2"
                value={pw1}
                onChange={(e) => setPw1(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>

            <div>
              <label
                htmlFor="pw2"
                className="mb-1 block text-sm font-medium text-slate-200"
              >
                Confirm new password
              </label>
              <input
                id="pw2"
                type="password"
                placeholder="Confirm new password"
                className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none ring-sky-500 focus:ring-2"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>

            <button
              disabled={savingPw}
              className="inline-flex w-full items-center justify-center rounded-lg bg-sky-500 px-4 py-2.5 text-sm font-semibold text-slate-950 hover:bg-sky-400 disabled:opacity-60"
              type="submit"
            >
              {savingPw ? "Saving…" : "Update password"}
            </button>
          </form>

          <div className="mt-4 text-sm">
            <button
              type="button"
              onClick={() => {
                // let them back to normal login if they somehow landed here without a valid session
                setIsRecovery(false);
                setResetErr("");
                setResetMsg("");
              }}
              className="text-sky-400 hover:text-sky-300 underline"
            >
              Back to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Normal login UI + "Forgot password?"
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-950/95 p-8 shadow-[0_24px_60px_rgba(15,23,42,0.95)]">
        <h1 className="text-2xl font-bold tracking-tight text-slate-50">
          Welcome back
        </h1>
        <p className="mt-1 text-sm text-slate-400">
          Don’t have an account?{" "}
          <Link
            to="/signup"
            className="text-sky-400 hover:text-sky-300 underline"
          >
            Create one
          </Link>
        </p>

        {note && (
          <div className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
            {note}
          </div>
        )}

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <label
              htmlFor="email"
              className="mb-1 block text-sm font-medium text-slate-200"
            >
              Email
            </label>
            <input
              id="email"
              ref={emailRef}
              type="email"
              placeholder="you@example.com"
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none ring-sky-500 focus:ring-2"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="mb-1 block text-sm font-medium text-slate-200"
            >
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPwd ? "text" : "password"}
                placeholder="••••••••"
                className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 pr-12 text-sm text-slate-100 outline-none ring-sky-500 focus:ring-2"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute inset-y-0 right-0 px-3 text-xs font-medium text-slate-400 hover:text-slate-200"
                aria-label={showPwd ? "Hide password" : "Show password"}
              >
                {showPwd ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label
              htmlFor="remember"
              className="inline-flex items-center gap-2 text-sm text-slate-400"
            >
              <input
                id="remember"
                type="checkbox"
                className="h-4 w-4 rounded border-slate-600 bg-slate-900"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              Remember me
            </label>

            <button
              type="button"
              onClick={onReset}
              className="text-sm text-sky-400 hover:text-sky-300 underline"
              disabled={sendingLink}
            >
              {sendingLink ? "Sending…" : "Forgot password?"}
            </button>
          </div>

          <button
            disabled={loading}
            className="inline-flex w-full items-center justify-center rounded-lg bg-sky-500 px-4 py-2.5 text-sm font-semibold text-slate-950 hover:bg-sky-400 disabled:opacity-60"
            type="submit"
          >
            {loading ? "Logging in..." : "Log in"}
          </button>
        </form>
      </div>
    </div>
  );
}
